import { CODE_PROGRAM_MAP } from '../../utils/dataNormalizer';

export const sanitizeBacSearchValue = (value) => String(value ?? '').replace(/\D/g, '').slice(0, 6);

export const pickSelectionValue = (selection, keys) => {
  if (!selection) return null;
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(selection, key)) continue;
    const value = selection[key];
    if (value === null || value === undefined) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    return value;
  }
  return null;
};

export const normalizeComparableStatus = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim().toLowerCase();
};

export const pickStatusValue = (row, keys) => {
  if (!row) return '';
  for (const key of keys) {
    const value = row?.[key];
    if (value === null || value === undefined) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    return value;
  }
  return '';
};

const normalizeStatusToken = (value) => normalizeComparableStatus(value).replace(/[^a-z]/g, '');
const normalizeDateOnly = (value) => {
  if (value === null || value === undefined) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  if (/^\d{2}\/\d{2}\/\d{2,4}$/.test(raw)) {
    const [month, day, yearRaw] = raw.split('/');
    const yearNum = Number(yearRaw);
    if (!Number.isFinite(yearNum)) return '';
    const year = yearRaw.length === 2 ? 2000 + yearNum : yearNum;
    return `${String(year).padStart(4, '0')}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10);
};

const isMismatchExcludedEnrollmentStatus = (row) => {
  const enrollmentStatus = pickStatusValue(row, [
    'enrollmentStatusCd',
    'enrollmentStatus',
    'programStatusCd',
    'programStatus',
  ]);
  const token = normalizeStatusToken(enrollmentStatus);
  if (!token) return false;
  return token === 'optedout' || token === 'optout' || token.startsWith('cancelled') || token.startsWith('canceled');
};

const isWebsiteProgram = (row) => {
  const programName =
    row?.programName ||
    row?.programLabel ||
    CODE_PROGRAM_MAP[row?.programCode] ||
    CODE_PROGRAM_MAP[row?.program] ||
    row?.program ||
    '';
  const normalized = normalizeComparableStatus(programName);
  const programCode = String(row?.programCode || row?.programCd || row?.program || '').trim().toUpperCase();
  return normalized === 'website' || programCode === 'DDSDWC';
};

const isPendingLiveStatus = (value) => {
  const normalized = normalizeComparableStatus(value);
  return normalized.includes('pending') && normalized.includes('live');
};

const isLiveStatus = (value) => {
  const normalized = normalizeComparableStatus(value);
  return normalized.includes('live') || normalized === 'active';
};

export const computeStatusMatchState = (row) => {
  if (!row) return null;
  if (isMismatchExcludedEnrollmentStatus(row)) return null;
  const dpe = normalizeComparableStatus(
    pickStatusValue(row, [
      'productSelectionStatus',
      'productSelectionStatusCd',
      'status',
      'selectionStatus',
      'productStatus',
    ])
  );
  let di = normalizeComparableStatus(
    pickStatusValue(row, [
      'diStatus',
      'diStatusCd',
      'diSelectionStatus',
      'diSelectionStatusCd',
      'diProductStatus',
      'diProductStatusCd',
      'diStatusLabel',
      'diStatusName',
      'diStatusText',
      'diStatusDescription',
    ])
  );
  if (!di && row?.di && typeof row.di === 'object') {
    di = normalizeComparableStatus(
      pickStatusValue(row.di, ['diStatus', 'status', 'selectionStatus', 'productStatus'])
    );
  }
  const dpeEffectiveDate = pickStatusValue(row, ['effectiveDate', 'effective_date', 'productEffectiveDate']);
  const diEffectiveDate = pickStatusValue(row, ['diEffectiveDate', 'diEffectiveDateUtc', 'diProductEffectiveDate']);
  if (
    isWebsiteProgram(row) &&
    isPendingLiveStatus(dpe) &&
    isLiveStatus(di) &&
    normalizeDateOnly(dpeEffectiveDate) &&
    normalizeDateOnly(dpeEffectiveDate) === normalizeDateOnly(diEffectiveDate)
  ) {
    return 'match';
  }
  if (!dpe || !di) return null;
  return dpe === di ? 'match' : 'mismatch';
};

export const resolveProgramNameKey = (row) => {
  if (!row) return null;
  return (
    row.programName ||
    row.programLabel ||
    CODE_PROGRAM_MAP[row.programCode] ||
    CODE_PROGRAM_MAP[row.program] ||
    row.program ||
    null
  );
};
